<template>
    <div>
        <form action="" class="form">
            <div class="notification is-success" role="alert" v-if="message">
                <span v-text="message"></span>
            </div>
            <label v-if="!user" class="label">
                {{ ttt("Reply to") }}

                <input
                    v-model="model.reply_to"
                    :placeholder="ttt('Reply to')"
                    autocomplete="off"
                    class="input"
                    name="reply_to"
                    title=""
                    type="email"
                />
            </label>
            <div class="error">
                <p
                    v-for="error in errors.get('reply_to')"
                    class="is-danger help"
                >
                    {{ error }}
                </p>
            </div>

            <div class="wrapper-checkbox-radio">
                <div class="title">{{ ttt("Regarding") }}*</div>
                <input
                    id="subject-finances"
                    v-model="model.subject"
                    class="radio"
                    name="subject"
                    type="radio"
                    value="finances"
                />
                <label class="label-def" for="subject-finances">
                    {{ ttt("Account and Billing") }}
                </label>
                <br /><br />
                <input
                    id="subject-it"
                    v-model="model.subject"
                    class="radio"
                    name="subject"
                    type="radio"
                    value="it"
                />
                <label class="label-def" for="subject-it">
                    {{ ttt("Technical Support") }}
                </label>
                <br /><br />
                <input
                    id="subject-proposals"
                    v-model="model.subject"
                    class="radio"
                    name="subject"
                    type="radio"
                    value="proposals"
                />
                <label class="label-def" for="subject-proposals">
                    {{ ttt("Offers and suggestions") }}
                </label>
            </div>

            <div class="error">
                <p
                    v-for="error in errors.get('subject')"
                    class="is-danger help"
                >
                    {{ error }}
                </p>
            </div>

            <label class="label" for="message">
                <span>{{ ttt("Description") }}*</span>
            </label>
            <div class="control">
                <textarea
                    id="message"
                    v-model="model.message"
                    :placeholder="ttt('Type your message')"
                    class="textarea"
                    cols="30"
                    name="message"
                    rows="8"
                    style="width: 100%"
                    title=""
                ></textarea>
            </div>
            <div class="error">
                <p
                    v-for="error in errors.get('message')"
                    class="is-danger help"
                >
                    {{ error }}
                </p>
            </div>

            <div style="margin-top: 1rem">
                <div :id="turnstileId"></div>
            </div>

            <div class="error">
                <p
                    v-for="error in errors.get('cf-turnstile-response')"
                    class="is-danger help"
                >
                    {{ error }}
                </p>
            </div>

            <button class="btn-submit" @click.prevent="store">
                {{ ttt("Submit") }}
            </button>
        </form>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onBeforeMount, reactive, onMounted } from "vue";
import { UserResource } from "@/modules/users/interfaces";
import { i18n } from "@/services/i18n";
import http from "@/services/http";
import { me as meApi } from "@/routes/api/users";
import { store as feedbackStoreApi } from "@/routes/api/feedbacks";
import { Errors } from "@/modules/api/errors";
import { useId } from "@/composables/useId";
import { SuccessResponse } from "@/api/v1/feedbacks/responses";
import { StoreRequest } from "@/api/v1/feedbacks/requests";
import { waitForTurnstile } from "@/services/turnstile";

type feedbackValidationKey = "subject" | "message" | "reply_to";

type feedbackValidation = Record<feedbackValidationKey, string[]>;

export default defineComponent({
    name: "feedbacks-form",
    setup() {
        const message = ref("");
        const model = reactive<StoreRequest>({
            "subject": "",
            "message": "",
            "reply_to": "",
            "cf-turnstile-response": "",
        });
        const turnstileId = useId();

        const validation = ref<feedbackValidation>({} as feedbackValidation);
        const errors = ref<Errors>(new Errors());
        const user = ref<UserResource | null>(null);

        let widgetId: string | null = null;

        onBeforeMount(() => {
            fetchUser();
        });

        onBeforeMount(() => {
            i18n.add("ru", {
                "Submit": "Отправить",
                "Type your message": "Ваше сообщение",
                "Account and Billing": "Финансовые вопросы",
                "Technical Support": "Технические вопросы",
                "Offers and suggestions": "Предложения и рекомендации",
                "Reply to": "Ответить на",
                "Regarding": "Тема",
            });

            i18n.add("uk", {
                "Submit": "Надіслати",
                "Type your message": "Введіть своє повідомлення",
                "Account and Billing": "Фінансові питання",
                "Technical Support": "Технічні питання",
                "Offers and suggestions": "Пропозиції та рекомендації",
                "Reply to": "Відповідати на",
                "Regarding": "Тема",
            });

            i18n.add("pl", {
                "Submit": "Wyślij",
                "Type your message": "Wpisz swoją wiadomość",
                "Account and Billing": "Rachunek i rozliczenia",
                "Technical Support": "Wsparcie techniczne",
                "Offers and suggestions": "Oferty i propozycje",
                "Reply to": "Odpowiedź na",
                "Regarding": "Dotyczące",
            });
        });

        onMounted(async () => {
            await waitForTurnstile();

            widgetId = globalThis.turnstile.render(`#${turnstileId}`, {
                sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
                size: "flexible",
                callback: function (token: string) {
                    model["cf-turnstile-response"] = token;
                },
            });
        });

        async function fetchUser() {
            await http
                .get(meApi.url())
                .then((r) => {
                    user.value = r.data;
                })
                .catch((e) => {
                    if (401 === e.response.status) {
                        return;
                    }
                });
        }

        async function store() {
            errors.value = new Errors();

            http.post<StoreRequest, { data: SuccessResponse }>(
                feedbackStoreApi.url(),
                model
            )
                .then((r) => {
                    Object.assign(model, {
                        "subject": "",
                        "message": "",
                        "reply_to": "",
                        "cf-turnstile-response": "",
                    });

                    message.value = r.data.message;
                    setTimeout(() => (message.value = ""), 2000);
                })
                .catch((e) => {
                    if (422 === e.response.status) {
                        errors.value = new Errors(e.response.data.errors);
                    }

                    if (widgetId != null && globalThis.turnstile) {
                        globalThis.turnstile.reset(widgetId);
                    }
                });
        }

        return {
            model,
            user,
            validation,
            errors,
            message,
            store,
            ttt: i18n.t,
            turnstileId,
        };
    },
});
</script>
